import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Users, Loader2 } from "lucide-react";

// PLAN1.4.2 – Schritt nach der Doku-Welt: simulierte AUTOMATISCHE Mitarbeiter-
// Zuweisung. Reine Anzeige – keine Auswahl, keine echte Zuweisung, kein Backend,
// KEIN „Weiter"-Button. Der Screen wird BEWUSST 8 Sekunden gezeigt (mit Countdown
// + dezenter Fortschrittsanzeige), damit der Nutzer den Mehrwert erfasst:
// „CLartext erkennt selbstständig, wer welche Unterweisung benötigt." Danach
// AUTOMATISCHER Übergang zum Abschluss-Screen.
const DEMO_MITARBEITER = ["Max Mustermann", "Anna Schmidt", "Peter Weber"];
const TOTAL_SECONDS = 8;
const REVEAL_MS = 500;

export default function MitarbeiterZuweisungDemo({ onDone }) {
  const [revealed, setRevealed] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [progress, setProgress] = useState(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    // Mitarbeiter gestaffelt einblenden.
    let rc = 0;
    const revealTimer = setInterval(() => {
      rc += 1;
      setRevealed(rc);
      if (rc >= DEMO_MITARBEITER.length) clearInterval(revealTimer);
    }, REVEAL_MS);

    // Dezente Fortschrittsanzeige (CSS-Transition über 8 s).
    const progStart = setTimeout(() => setProgress(100), 60);

    // Sichtbarer Countdown.
    let s = TOTAL_SECONDS;
    const countdown = setInterval(() => {
      s -= 1;
      setSecondsLeft(s > 0 ? s : 0);
      if (s <= 0) clearInterval(countdown);
    }, 1000);

    // Automatischer Übergang nach exakt 8 Sekunden.
    const advance = setTimeout(() => onDoneRef.current(), TOTAL_SECONDS * 1000);

    return () => {
      clearInterval(revealTimer);
      clearTimeout(progStart);
      clearInterval(countdown);
      clearTimeout(advance);
    };
  }, []);

  return (
    <section
      className="wrap reveal in"
      data-testid="lp-assign-view"
      style={{ maxWidth: 680, margin: "0 auto", padding: "36px 16px 64px", textAlign: "center" }}
    >
      <span
        className="pill"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", borderRadius: 999, padding: "5px 14px", fontSize: "0.8rem", fontWeight: 600 }}
      >
        <Users size={14} /> Automatische Zuweisung
      </span>

      <h2 style={{ fontSize: "1.6rem", fontWeight: 700, color: "#0f172a", margin: "18px 0 8px" }}>
        Ihre Dokumentationswelt ist fertig.
      </h2>
      <p style={{ color: "#475569", fontSize: "1rem", lineHeight: 1.5, margin: "0 auto 26px", maxWidth: 540 }}>
        Die Dokumentationswelt ist vollständig. CLartext erkennt automatisch die betroffenen Mitarbeiter, weist Unterweisungen zu und erstellt alle Nachweise.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 420, margin: "0 auto 22px" }}>
        {DEMO_MITARBEITER.map((name, i) => {
          const shown = revealed > i;
          return (
            <div
              key={i}
              data-testid={`lp-assign-employee-${i}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                border: "1px solid #e2e8f0", borderRadius: 12, padding: "13px 16px", background: "#fff",
                opacity: shown ? 1 : 0,
                transform: shown ? "translateY(0)" : "translateY(6px)",
                transition: "opacity 0.35s ease, transform 0.35s ease",
              }}
            >
              <span style={{ fontWeight: 600, color: "#0f172a" }}>{name}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#047857", fontWeight: 600, fontSize: "0.9rem" }}>
                <CheckCircle2 size={18} /> Zugewiesen
              </span>
            </div>
          );
        })}
      </div>

      <p
        data-testid="lp-assign-status"
        style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#64748b", fontSize: "0.9rem", margin: "0 0 14px" }}
      >
        <Loader2 size={15} style={{ animation: "lp3-spin .9s linear infinite" }} /> Unterweisungen werden automatisch zugewiesen …
      </p>

      <div style={{ height: 4, background: "#e2e8f0", borderRadius: 999, overflow: "hidden", maxWidth: 300, margin: "0 auto 8px" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "#10b981", transition: `width ${TOTAL_SECONDS}s linear` }} />
      </div>
      <div data-testid="lp-assign-countdown" style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
        Weiter in {secondsLeft} Sekunde{secondsLeft === 1 ? "" : "n"} …
      </div>
    </section>
  );
}
